import type { RuleGraphNode } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";

import { RULE_GRAPH_INITIAL_INSET } from "./ruleGraphViewport/initialRuleGraphViewport";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RuleGraphViewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * The viewport that keeps the graph clear of the details panel once it has
 * narrowed the canvas to `canvasSize`. The panel takes its width out of the
 * canvas, so anything right of the new edge would sit behind it.
 *
 * In order of preference: leave the view alone when every node is already
 * inside the narrowed canvas; pan left when the graph fits at the current
 * zoom; zoom out (never past `minZoom`) until it fits; and when even that
 * cannot fit the graph, keep at least the node the panel describes in view.
 * The graph's top-left stays where it was on screen while zooming, so the
 * rows do not jump vertically. Pure so the camera is testable without React
 * Flow.
 */
export function viewportClearOfDetailsPanel({
  viewport,
  bounds,
  canvasSize,
  minZoom,
  selectedNodeRect,
  marginPx = RULE_GRAPH_INITIAL_INSET.left,
}: {
  viewport: RuleGraphViewport;
  bounds: Rect;
  canvasSize: { width: number; height: number };
  minZoom: number;
  selectedNodeRect?: Rect;
  marginPx?: number;
}): RuleGraphViewport {
  const right = (bounds.x + bounds.width) * viewport.zoom + viewport.x;
  const limit = canvasSize.width - marginPx;
  if (right <= limit) {
    return viewport;
  }

  const room = canvasSize.width - 2 * marginPx;
  const zoom = Math.min(
    viewport.zoom,
    Math.max(minZoom, room / Math.max(1, bounds.width))
  );
  const left = bounds.x * viewport.zoom + viewport.x;
  const top = bounds.y * viewport.zoom + viewport.y;
  // Scale about the graph's on-screen top-left corner.
  let x = left - bounds.x * zoom;
  const y = top - bounds.y * zoom;
  const scaledRight = (bounds.x + bounds.width) * zoom + x;
  if (scaledRight > limit) {
    // Pan left by the overflow, but never past the left margin.
    x -= Math.min(
      scaledRight - limit,
      Math.max(0, bounds.x * zoom + x - marginPx)
    );
  }
  if (selectedNodeRect !== undefined) {
    // The graph still does not fit at the minimum zoom: the node the panel
    // describes is the one that must stay out from behind it.
    const nodeRight = (selectedNodeRect.x + selectedNodeRect.width) * zoom + x;
    if (nodeRight > limit) {
      x -= nodeRight - limit;
    }
  }
  return { x, y, zoom };
}

/**
 * What the canvas live region says as the panel opens and closes, naming the
 * step it describes.
 */
export function detailsPanelAnnouncement(
  node: Pick<RuleGraphNode, "label">,
  state: "opened" | "closed"
): string {
  return state === "opened"
    ? `Opened details for ${node.label}`
    : `Closed details for ${node.label}`;
}
